/**
 * Mushroom Coffee — theme.js
 * All non-critical JS. Loaded with defer — never blocks rendering.
 * Vanilla JS only, no framework dependencies.
 */

'use strict';

/* ==========================================================================
   Utility helpers
   ========================================================================== */

const $ = (selector, context = document) => context.querySelector(selector);
const $$ = (selector, context = document) => [...context.querySelectorAll(selector)];

function debounce(fn, delay = 200) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

function trapFocus(container) {
  const focusable = $$('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])', container);
  if (!focusable.length) return;
  const first = focusable[0];
  const last  = focusable[focusable.length - 1];
  container.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
    }
  });
}

/* ==========================================================================
   Header — scroll shadow & sticky behaviour
   ========================================================================== */

function initHeader() {
  const header = $('.site-header');
  if (!header) return;

  const onScroll = debounce(() => {
    header.classList.toggle('site-header--scrolled', window.scrollY > 20);
  }, 50);

  window.addEventListener('scroll', onScroll, { passive: true });
}

/* ==========================================================================
   Mobile nav
   ========================================================================== */

function initMobileNav() {
  const menuBtn  = $('.mobile-menu-btn');
  const closeBtn = $('.mobile-nav__close');
  const nav      = $('.mobile-nav');
  const overlay  = $('.nav-overlay');

  if (!menuBtn || !nav) return;

  function open() {
    nav.classList.add('is-open');
    overlay?.classList.add('is-open');
    menuBtn.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    trapFocus(nav);
  }

  function close() {
    nav.classList.remove('is-open');
    overlay?.classList.remove('is-open');
    menuBtn.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    menuBtn.focus();
  }

  menuBtn.addEventListener('click', open);
  closeBtn?.addEventListener('click', close);
  overlay?.addEventListener('click', close);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && nav.classList.contains('is-open')) close();
  });
}

/* ==========================================================================
   Cart drawer
   ========================================================================== */

function initCartDrawer() {
  const drawer  = $('#cart-drawer');
  const overlay = $('#cart-overlay');
  const cartBtns = $$('[data-cart-open]');
  const closeBtn = drawer ? $('#cart-drawer-close', drawer) : null;

  if (!drawer) return;

  window.CartDrawer = {
    open() {
      drawer.classList.add('is-open');
      overlay?.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      drawer.removeAttribute('inert');
      document.body.style.overflow = 'hidden';
      trapFocus(drawer);
    },
    close() {
      drawer.classList.remove('is-open');
      overlay?.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      drawer.setAttribute('inert', '');
      document.body.style.overflow = '';
    }
  };

  cartBtns.forEach(btn => btn.addEventListener('click', () => window.CartDrawer.open()));
  closeBtn?.addEventListener('click', () => window.CartDrawer.close());
  overlay?.addEventListener('click', () => window.CartDrawer.close());

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
      window.CartDrawer.close();
    }
  });
}

/* ==========================================================================
   Product page — variant selection & image gallery
   ========================================================================== */

function initProductPage() {
  const productJsonEl = $('#product-json');
  if (!productJsonEl) return;

  let product;
  try {
    product = JSON.parse(productJsonEl.textContent);
  } catch (e) {
    console.warn('[theme] Could not parse product JSON:', e);
    return;
  }

  // Variant options
  const optionSelectors = $$('[data-option-selector]');
  const priceEl = $('.product-page__price');
  const comparePriceEl = $('.product-page__compare-price');
  const addBtn = $('[data-add-to-cart]');
  const addBtnText = $('[data-add-to-cart-text]');
  const mainImage = $('.product-page__main-image img');

  function getSelectedVariant() {
    const selectedOptions = optionSelectors.map(sel => sel.dataset.selectedValue || sel.value);
    return product.variants.find(v =>
      v.options.every((opt, i) => opt === selectedOptions[i])
    );
  }

  function updateForVariant(variant) {
    if (!variant) {
      if (addBtn) addBtn.disabled = true;
      if (addBtnText) addBtnText.textContent = 'Unavailable';
      return;
    }

    // Price
    const money = (cents) => '$' + (cents / 100).toFixed(2);
    if (priceEl) priceEl.textContent = money(variant.price);
    if (comparePriceEl) {
      if (variant.compare_at_price > variant.price) {
        comparePriceEl.textContent = money(variant.compare_at_price);
        comparePriceEl.hidden = false;
      } else {
        comparePriceEl.hidden = true;
      }
    }

    // Add to cart button
    if (addBtn) {
      addBtn.disabled = !variant.available;
      addBtn.dataset.variantId = variant.id;
    }
    if (addBtnText) {
      addBtnText.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
    }

    // Main image
    if (mainImage && variant.featured_image) {
      mainImage.src = variant.featured_image.src;
      mainImage.alt = variant.featured_image.alt || product.title;
    }

    // URL
    window.history.replaceState({}, '', `?variant=${variant.id}`);
  }

  // Option pill click
  $$('.variant-option').forEach(btn => {
    btn.addEventListener('click', () => {
      const position = btn.dataset.position;
      const value    = btn.dataset.value;

      // Update active state
      $$(`[data-option-selector][data-position="${position}"] .variant-option`).forEach(b => {
        b.classList.toggle('is-selected', b.dataset.value === value);
      });

      const selector = $(`[data-option-selector][data-position="${position}"]`);
      if (selector) selector.dataset.selectedValue = value;

      updateForVariant(getSelectedVariant());
    });
  });

  // Thumbnail gallery
  $$('.product-page__thumb').forEach(thumb => {
    thumb.addEventListener('click', () => {
      const src = thumb.dataset.src;
      const alt = thumb.dataset.alt;
      if (mainImage && src) {
        mainImage.src = src;
        mainImage.alt = alt || '';
      }
      $$('.product-page__thumb').forEach(t => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
    });
  });

  // Initialize with pre-selected or first variant
  const urlParams = new URLSearchParams(window.location.search);
  const preselectedId = urlParams.get('variant');
  const initial = preselectedId
    ? product.variants.find(v => v.id == preselectedId)
    : product.variants[0];

  if (initial) updateForVariant(initial);
}

/* ==========================================================================
   Quick add — product grid
   ========================================================================== */

function initQuickAdd() {
  $$('[data-quick-add]').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();

      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      btn.disabled = true;
      const original = btn.innerHTML;
      btn.innerHTML = '<span class="loading-spinner"></span>';

      try {
        await window.Cart.add({ id: variantId, quantity: 1 });
        btn.innerHTML = '&#10003;'; // checkmark
        setTimeout(() => {
          btn.innerHTML = original;
          btn.disabled = false;
        }, 1500);

        window.CartDrawer?.open();
      } catch (err) {
        console.error('[theme] Quick add failed:', err);
        btn.innerHTML = original;
        btn.disabled = false;
      }
    });
  });
}

/* ==========================================================================
   Scroll animations — intersection observer for fade-in
   ========================================================================== */

function initScrollReveal() {
  // Reveal styles live in theme.css + theme.liquid inline <style>.
  // JS only manages the .is-visible class toggle via IntersectionObserver.
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1 });

  $$('[data-reveal]').forEach(el => observer.observe(el));
}

/* ==========================================================================
   Marquee — pause on hover (CSS handles animation, JS adds a11y toggle)
   ========================================================================== */

function initMarquee() {
  $$('.marquee-bar__track').forEach(track => {
    // Duplicate content for seamless loop (if not already done server-side)
    if (!track.dataset.duplicated) {
      track.innerHTML += track.innerHTML;
      track.dataset.duplicated = 'true';
    }
  });
}

/* ==========================================================================
   Search — toggle search overlay
   ========================================================================== */

function initSearch() {
  const searchBtn   = $('[data-search-open]');
  const searchClose = $('[data-search-close]');
  const searchWrap  = $('#search-overlay');
  const searchInput = searchWrap ? $('input[type="search"]', searchWrap) : null;

  if (!searchBtn || !searchWrap) return;

  function openSearch() {
    searchWrap.classList.add('is-open');
    searchWrap.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    // Defer focus so the element is visible first
    requestAnimationFrame(() => searchInput?.focus());
  }

  function closeSearch() {
    searchWrap.classList.remove('is-open');
    searchWrap.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    searchBtn.focus();
  }

  searchBtn.addEventListener('click', openSearch);
  searchClose?.addEventListener('click', closeSearch);

  // Close on backdrop click (outside the inner panel)
  searchWrap.addEventListener('click', (e) => {
    if (e.target === searchWrap) closeSearch();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && searchWrap.classList.contains('is-open')) closeSearch();
  });
}

/* ==========================================================================
   Initialise all modules on DOMContentLoaded
   ========================================================================== */

document.addEventListener('DOMContentLoaded', () => {
  initHeader();
  initMobileNav();
  initCartDrawer();
  initProductPage();
  initQuickAdd();
  initScrollReveal();
  initMarquee();
  initSearch();
});
